import { useState, useEffect } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Text, TextInput, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';

import type { validateNewHighlightTime } from './Recordings/utils';
import { formatTime, parseFormattedTime } from './Recordings/utils';
import { useRealTimeStore } from '../Playbacks/store/realTimeStore';
import { useRecordingHighlightPreview } from './use-recording-highlight-preview';

/*
  This file is a clone of `packages/legacy/src/webapp/features/Highlights/Heatmaps/HighlightDescriptionCreator.tsx`.
  Both components are serving the same purpose - textarea and button composed to create highlight.
  Consider refactoring in near future.
*/

type NewTimeValidationError = ReturnType<typeof validateNewHighlightTime>;

const TIME_FORMAT_REGEXP = /^([0-5]?\d):([0-5]\d)$/;

function ErrorMessage({ children }: { children: NewTimeValidationError }): JSX.Element | null {
  const { t } = useTranslation();

  switch (children) {
    case 'START_EXCEEDS_MARKER':
      return <span>{t('highlights:single.create.error.clipStartExceedsMarker')}</span>;
    case 'END_EXCEEDS_MARKER':
      return <span>{t('highlights:single.create.error.clipEndExceedsMarker')}</span>;
    case 'TOO_SHORT':
      return <span>{t('highlights:single.create.error.clipTooShort')}</span>;
    case 'TOO_LONG':
      return <span>{t('highlights:single.create.error.clipTooLong')}</span>;
    default:
      return null;
  }
}

export interface HighlightTime {
  end: number;
  marker: number;
  start: number;
}

export interface HighlightCreatorProps {
  className?: string;
  isSubmitting: boolean;
  highlightType?: 'heatmap' | 'recording';
  newHighlightTime: {
    start: number;
    marker: number;
    end: number;
  } | null;
  newHighlightTimeError?: NewTimeValidationError;
  onNewHighlightTime: (({ start, end, marker }: HighlightTime) => void) | null;
  onSave: () => void;
  onClose: () => void;
  jumpTo?: (targetTime: number) => void;
  onPlay?: () => void;
  pause?: () => void;
  loaded?: boolean;
  isPlaying?: boolean;
}

const CreatorBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: ${tokens.spacer.x1};
`;

const ActionsContainer = styled.div`
  margin-top: ${tokens.spacer.x1};
  justify-content: end;
  align-items: center;
`;

const ActionButtons = styled.div`
  margin-top: ${tokens.spacer.x2};
  float: right;
  width: 100%;
`;

const SaveActions = styled.div`
  float: right;
  height: 100%;
  display: flex;
  align-items: center;
  z-index: 1;
  button {
    z-index: 1;
  }
`;

const PreviewButton = styled(IconButton)`
  float: left;
`;

const InputWrapper = styled.div`
  z-index: 1;
`;

const InnerLabel = styled.span`
  ${tokens.text('subtitle3')}
  color: ${tokens.color.text_light};
  position: absolute;
  z-index: 1;
  margin: ${tokens.spacer.x1pt5};
`;

const ShortInput = styled(TextInput)`
  width: 112px;
  height: 40px;
  input {
    padding: 0px 0px 0 ${tokens.spacer.x6};
  }
`;

const InputsBox = styled.div`
  display: flex;
  align-items: center;
`;

const ArrowWrapper = styled.div`
  margin: 0 ${tokens.spacer.x1};
  z-index: 1;
`;

const ArrowIcon = styled(Icon)`
  z-index: 1;
`;

const ButtonWithMargin = styled(Button)`
  margin-right: ${tokens.spacer.x1};
`;

const Error = styled(Text)`
  margin-top: ${tokens.spacer.x1};
`;

const TextWithMargin = styled(Text)`
  margin-right: ${tokens.spacer.x2};
`;

export const HighlightCreator = ({
  className,
  isSubmitting,
  highlightType = 'heatmap',
  newHighlightTime,
  newHighlightTimeError,
  onNewHighlightTime,
  onSave,
  onClose,
  jumpTo,
  onPlay,
  pause,
  loaded,
  isPlaying,
}: HighlightCreatorProps): JSX.Element => {
  const [inputValues, setInputValues] = useState({ end: '', start: '' });
  const currentTime = useRealTimeStore((state) => state.currentTime);
  const { isPlayingStarted, togglePlayPause } = useRecordingHighlightPreview({
    loaded,
    isPlaying,
    currentTime,
    newHighlightTime,
    onPlay,
    pause,
    jumpTo,
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (newHighlightTime) {
      setInputValues({
        end: formatTime(newHighlightTime.end),
        start: formatTime(newHighlightTime.start),
      });
    }
  }, [newHighlightTime]);

  const handleInputChange =
    (propName: 'start' | 'end') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      setInputValues((previousValues) => ({ ...previousValues, [propName]: newValue }));

      if (!TIME_FORMAT_REGEXP.test(newValue) || !newHighlightTime) {
        return;
      }

      if (onNewHighlightTime) {
        onNewHighlightTime({
          ...newHighlightTime,
          [propName]: parseFormattedTime(newValue),
        });
      }
    };

  return (
    <CreatorBox className={className}>
      <ActionsContainer>
        {highlightType === 'recording' ? (
          <InputsBox>
            <InputWrapper>
              <InnerLabel>{t('highlights:recordings.start')}</InnerLabel>
              <ShortInput
                aria-label="Clip duration start"
                size="s"
                value={inputValues.start}
                onChange={handleInputChange('start')}
              />
            </InputWrapper>
            <ArrowWrapper>
              <ArrowIcon icon="arrow_right" />
            </ArrowWrapper>
            <InputWrapper>
              <InnerLabel>{t('highlights:recordings.end')}</InnerLabel>
              <ShortInput
                aria-label="Clip duration end"
                size="s"
                value={inputValues.end}
                onChange={handleInputChange('end')}
              />
            </InputWrapper>
          </InputsBox>
        ) : null}
        {highlightType !== 'recording' ? (
          <TextWithMargin textType="caption">{t('highlights:single.create.copy')}</TextWithMargin>
        ) : null}
        <ActionButtons>
          {highlightType === 'recording' && (
            <PreviewButton
              icon={isPlaying && isPlayingStarted ? 'pause_alt' : 'play_alt'}
              size="s"
              tooltipText={
                isPlaying && isPlayingStarted
                  ? t('highlights:recordings.pause')
                  : t('highlights:recordings.preview')
              }
              onClick={togglePlayPause}
            />
          )}
          <SaveActions>
            <ButtonWithMargin kind="ghost" size="s" onClick={onClose}>
              {t('common:player.cancel')}
            </ButtonWithMargin>
            <Button
              isDisabled={isSubmitting}
              type="submit"
              kind="primary"
              size="s"
              data-testid="create-highlight"
              onClick={onSave}
            >
              {t('common:player.save')}
            </Button>
          </SaveActions>
        </ActionButtons>
      </ActionsContainer>
      {highlightType === 'recording' && newHighlightTimeError ? (
        <Error as="p" textType="caption" textColor="text_default">
          <ErrorMessage>{newHighlightTimeError}</ErrorMessage>
        </Error>
      ) : null}
    </CreatorBox>
  );
};
